import { Application } from 'pixi.js';
// 글꼴은 게임에 내장한다(디스코드 액티비티 안에서는 외부 글꼴 서버를 못 부른다).
// 브라우저는 화면에 실제로 나온 글자가 든 조각만 받는다.
import '@fontsource-variable/google-sans';
import '@fontsource-variable/noto-sans-kr';
import './styles.css';

import { CONFIG } from './config';
import { bakeShapeTextures, preloadSprites } from './core/assets';
import { Input } from './core/input';
import type { CharacterDef, EnemyDef, FusionDef, PassiveDef, RunResult, WeaponDef } from './core/types';
import { Game, type GameData, type GameState } from './game/game';
import type { LevelUpChoice } from './game/levelup';
import { Ui } from './ui/ui';
import { initPlatform } from './platform/discord';
import { audio } from './core/audio';

import charactersJson from './data/characters.json';
import weaponsJson from './data/weapons.json';
import passivesJson from './data/passives.json';
import enemiesJson from './data/enemies.json';
import fusionsJson from './data/fusions.json';
import fxWarm from './data/fxwarm.json';
import { attachAutoResolution } from './core/autores';
import { DisplayController } from './core/display';

const params = new URLSearchParams(location.search);
const DEV = params.get('dev') === '1';
const SEED = params.has('seed') ? Number(params.get('seed')) >>> 0 : undefined;

async function boot(): Promise<void> {
  const host = document.getElementById('canvas-host')!;
  const uiRoot = document.getElementById('ui-root')!;

  const app = new Application();
  await app.init({
    background: CONFIG.BG_COLOR,
    resizeTo: window,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    preference: 'webgl',
    powerPreference: 'high-performance',
  });
  host.appendChild(app.canvas);
  const display = new DisplayController(app.ticker);

  bakeShapeTextures(app.renderer);

  const data: GameData = {
    characters: charactersJson as unknown as CharacterDef[],
    weapons: weaponsJson as unknown as WeaponDef[],
    passives: passivesJson as unknown as PassiveDef[],
    enemies: enemiesJson as unknown as EnemyDef[],
    fusions: fusionsJson as unknown as FusionDef[],
  };

  // sprite 경로가 채워져 있으면 미리 로드하고, 없거나 실패하면 도형으로 돈다.
  await preloadSprites([
    ...data.characters.map((c) => c.sprite),
    ...data.weapons.map((w) => w.sprite),
    ...data.passives.map((p) => p.sprite),
    ...data.enemies.map((e) => e.sprite),
    ...data.fusions.map((f) => f.sprite),
  ]);

  // 음원 파일(검 소리·배경음)은 미리 받아 두고, 소리는 첫 입력에 연다(브라우저 규칙).
  audio.preload();
  const unlock = (): void => audio.unlock();
  window.addEventListener('pointerdown', unlock, { capture: true });
  window.addEventListener('keydown', unlock, { capture: true });

  const platform = await initPlatform(DEV);
  const input = new Input(host);

  let lastResult: RunResult | null = null;
  let lastCharacterId = data.characters[0]?.id ?? '';

  const ui = new Ui(
    uiRoot,
    {
      onPickCharacter(id) {
        lastCharacterId = id;
        ui.hideAll();
        ui.setHudVisible(true);
        // 3단계: 판이 시작될 때 서버 세션을 연다 — 끝날 때 열면 서버 경과가 0이라 다 거부된다
        submitted = false;
        submitting = false;
        runGeneration++;
        lastResult = null;
        platform.startRun();
        game.start(id, SEED);
        audio.startMusic();
      },
      onPickUpgrade(choice: LevelUpChoice) {
        game.applyChoice(choice);
      },
      onResume() {
        ui.hidePause();
        game.resume();
      },
      onQuit() {
        game.quitToMenu();
      },
      onRetry() {
        ui.hideAll();
        ui.setHudVisible(true);
        submitted = false;
        submitting = false;
        runGeneration++;
        lastResult = null;
        platform.startRun();
        game.start(lastCharacterId, SEED);
        audio.startMusic();
      },
      onResubmit() {
        if (lastResult) void submit(lastResult);
      },
      onPauseRequest() {
        if (game.state === 'playing') game.pause();
        else if (game.state === 'paused') {
          ui.hidePause();
          game.resume();
        }
      },
      onActive(i: number) {
        game.useActive(i);
      },
      onDash() {
        game.useDash();
      },
    },
    DEV,
    display,
  );
  ui.setCatalog(data);

  // 한 판에 한 번만 보낸다. 보내는 중이면 겹쳐 보내지 않고, 저장된 뒤에는 다시 안 보낸다
  // (재전송 단추 연타 · 결과 화면이 두 번 뜨는 경우).
  let submitting = false;
  let submitted = false;
  let runGeneration = 0;

  /** 다시 보내도 소용없는 이유 — 단추를 감추고 무슨 뜻인지 적어 준다 */
  const DEAD_END: Record<string, string> = {
    session_used: '이미 저장된 판이다.',
    // 판을 시작할 때 세션을 못 열었다. 끝나고 새로 열어도 서버가 "너무 빠르다" 며 막는다
    session_failed: '판을 시작할 때 서버에 닿지 못해 이 판은 저장하지 못했다.',
    // 서버가 다시 켜졌거나(세션은 메모리에만 있다) 6시간이 지났다
    no_session: '서버에 이 판 기록이 없다 — 저장하지 못했다.',
    too_many_tries: '이 판은 더 보낼 수 없다.',
    session_expired: '기록 보관 시간이 지나 이 판은 저장하지 못했다.',
  };

  async function submit(result: RunResult): Promise<void> {
    if (platform.offline) {
      ui.setSubmitStatus(platform.offlineReason, 'muted');
      ui.setResubmitVisible(false);
      return;
    }
    if (submitting || submitted) return;
    submitting = true;
    const generation = runGeneration;
    ui.setSubmitStatus('점수 전송 중…', 'muted');
    try {
      const out = await platform.submitScore(result);
      if (generation !== runGeneration) return;
      if (out.ok) {
        submitted = true;
        const rank = out.rank ? ` · ${out.rank}위` : '';
        ui.setSubmitStatus((out.best ? '신기록 저장 완료!' : '점수 저장 완료') + rank, 'ok');
        ui.setResubmitVisible(false);
      } else if (DEAD_END[out.reason]) {
        submitted = true; // 더 해 볼 것이 없다
        ui.setSubmitStatus(DEAD_END[out.reason], 'muted');
        ui.setResubmitVisible(false);
      } else {
        ui.setSubmitStatus(`전송 실패: ${out.reason}`, 'error');
        ui.setResubmitVisible(true);
      }
    } finally {
      if (generation === runGeneration) submitting = false;
    }
  }

  const game = new Game(
    app,
    data,
    {
      onHud(snap) {
        ui.updateHud(snap);
      },
      onLevelUp(choices, source) {
        ui.showLevelUp(choices, game.level, source);
      },
      onBanner(text, sub, color, kind, image) {
        ui.showBanner(text, sub, color, kind, image);
      },
      onOver(result) {
        lastResult = result;
        const name = data.characters.find((c) => c.id === result.characterId)?.name ?? result.characterId;
        ui.showResult(result, name);
        void submit(result);
      },
      onStateChange(state: GameState) {
        // 배경음: 일시정지 동안 작게, 죽으면 천천히 사라지고, 나가면 끈다
        audio.setMusicDucked(state === 'paused');
        if (state === 'over') audio.stopMusic(2.2);
        else if (state === 'idle') audio.stopMusic(0.6);

        if (state === 'idle') {
          ui.showCharacterSelect(data.characters);
        } else if (state === 'paused') {
          ui.showPause();
        } else if (state === 'playing') {
          // 어떤 경로로 시작하든 오버레이는 확실히 걷어낸다
          ui.hideAll();
          ui.setHudVisible(true);
        }
      },
    },
    input,
  );

  game.prepareVisuals();
  // 도트 캐릭터 아틀라스. 없으면 조용히 도형으로 돌아간다.
  await game.loadCharacterArt();
  // 그림 이펙트(여제의 명령 번개). 없으면 선으로 대신 그린다.
  await game.loadFxArt();

  // 조이스틱은 게임 상태와 무관하게 매 프레임 그린다.
  app.ticker.add(() => ui.updateJoystick(input));

  /** 개발자 패널 열고 닫기 (개발 모드에서만 채워진다) */
  let devToggle: (() => void) | null = null;

  input.onKey = (code) => {
    if (code === 'Backquote' && devToggle) {
      devToggle();
      return;
    }
    // 1.24 Shift = 대시(누구나). 액티브 스킬은 먼저 얻은 것이 Space, 다음이 E
    if (code === 'ShiftLeft' || code === 'ShiftRight') {
      game.useDash();
      return;
    }
    if (code === 'Space' || code === 'KeyE') {
      game.useActive(code === 'KeyE' ? 1 : 0);
      return;
    }
    if (code === 'Escape' || code === 'KeyP') {
      if (game.state === 'playing') game.pause();
      else if (game.state === 'paused') {
        ui.hidePause();
        game.resume();
      }
      return;
    }
    if (code === 'KeyM') {
      ui.setSoundMode(audio.cycleMode());
      return;
    }
    const m = /^Digit([1-9])$/.exec(code);
    if (m && !ui.pickUpgradeByIndex(Number(m[1]) - 1)) ui.pickCharacterByIndex(Number(m[1]) - 1);
  };

  // 폭발 그림을 미리 굽는다(약 1초) — 판 도중 처음 보는 폭발을 굽느라 멈칫하지 않게
  await game.warmFx(fxWarm);
  // 버벅일 때만 그리는 해상도를 눈에 띄지 않게 낮춘다(판정과 무관)
  const autoRes = attachAutoResolution(app, () => game.state === 'playing', () => game.viewZoom < 1, () => display.frameLimit);

  ui.showCharacterSelect(data.characters);

  if (DEV) {
    console.info('[dev] 개발 모드로 실행 중. 유저:', platform.user);
    // 개발 모드에서만 콘솔로 내부 상태를 들여다볼 수 있게 노출한다.
    const w = window as unknown as Record<string, unknown>;
    w.__game = game;
    w.__audio = audio;
    w.__autoRes = autoRes;
    // 밸런스 실험용 — 콘솔에서 CONFIG 값을 바꿔 본다(바꿨으면 새로고침하고 잴 것)
    w.__CONFIG = CONFIG;
    // 밸런스 시뮬레이터는 개발 모드에서만 불러온다(배포 번들에 안 들어간다).
    const sim = await import('./dev/sim');
    w.__sim = (id: string, seed?: number) => sim.runSim(game, id, seed);
    w.__simAll = () => sim.runSimAll(game, data.characters.map((c) => c.id));
    const allDefs = [...data.weapons, ...data.fusions];
    const fusionIds = new Set(data.fusions.map((f) => f.id));
    // at = 적을 몇 초 시점만큼 단단하게 할지 (기본 600). 고유 스킬처럼 센 것은 600 에서 전부 잡혀 차이가 안 보이므로 900 으로 잰다
    w.__measure = (id: string, level?: number, picks?: string[], at = 600) =>
      sim.measureWeapon(game, allDefs, fusionIds, id, level, 12, at, picks);
    w.__measurePaths = (id: string, at = 600) => sim.measurePaths(game, allDefs, fusionIds, id, 12, at);
    // 변신 스킬(회광반조): 변신한 채 / 잔불만 / 한 주기 평균 — __measureForm('uniq_hoegwang', 1, [], 900)
    w.__measureForm = (id: string, level?: number, picks?: string[], at = 600) =>
      sim.measureForm(game, allDefs, fusionIds, id, level, picks, at);
    w.__measureAll = () => sim.measureAll(game, allDefs, fusionIds);
    w.__soundDensity = (ids?: string[]) => sim.measureSoundDensity(game, audio, allDefs, fusionIds, ids);
    // 피하는 자동 조종으로 한 판 (invincible 이면 죽지 않고 분마다 받은 피해만 센다)
    w.__survive = (seed = 1, opts?: { invincible?: boolean; maxSec?: number; character?: string }) =>
      sim.survive(game, seed, opts);
    // 위와 같은 판을 나눠 돌린다 — job.advance(초) 가 true 면 끝, job.report (긴 판은 콘솔 도우미 __long 으로)
    w.__surviveJob = (seed = 1, opts?: { invincible?: boolean; maxSec?: number; character?: string }) =>
      sim.surviveJob(game, seed, opts);
    // 이펙트 도감: 스킬마다 구운 프레임을 한 화면에 늘어놓는다
    const gallery = await import('./dev/gallery');
    w.__gallery = (only?: string[]) => gallery.showGallery(allDefs, only);
    if (params.has('gallery')) {
      const only = params.get('gallery')!.split(',').filter((v) => v && v !== '1');
      gallery.showGallery(allDefs, only);
    }
    // 개발자 패널: 레벨·무기·패시브를 마음대로 (` 키 또는 🛠 버튼)
    const devpanel = await import('./dev/devpanel');
    devToggle = devpanel.mountDevPanel(uiRoot, game, data).toggle;
    console.info('[dev] __sim("sohwa") / __simAll() / __measure("compress_shot") / __measurePaths("compress_shot") / __measureAll() / __gallery() / __soundDensity()');
  }
}

void boot();
